import TextField from "@/common/components/TextField";
import { useCompanyUsers } from "@/common/hooks/queries/useCompanyUsers";
import { Mapper } from "@/common/services/mapper";
import { CompanyUserData } from "@/features/privateCompanies/types/companyUsers.type";
import { ConsumptionValidationSchema } from "../schemas/create.schema";
import { useFormContext } from "react-hook-form";
import SelectController from "@/common/components/SelectController";
import EquipmentInfo from "./EquipmentInfo";
import { useEstablishmentCompanies } from "@/features/establishments/hooks/useEstablishmentCompanies";
import { EstablishmentCompanyTransformer } from "@/features/establishments/services/establishmentCompanyTransformer";
import useDebounce from "@/common/hooks/useDebounce";
import { useCardDetails } from "@/features/cards/hooks/useCardDetails";
import { useEffect } from "react";
import type { CompanyData } from "@/features/companies/types/companies.type";
import { cardStatusAvailableToConsumption } from "@/features/cards/constants/cardStatusAvailableToConsumption.constant";

interface EssentialInformationFormProps {
  clearForm: () => void;
}

const EssentialInformationForm = ({ clearForm }: EssentialInformationFormProps) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<ConsumptionValidationSchema>()

  const cardIdentifierFromForm = watch('card_identifier');

  const [debouncedCardIdentifier, setDebouncedCardData] = useDebounce(cardIdentifierFromForm || '');
  const [searchUsername, setSearchUsername] = useDebounce('');
  const [searchEstablishment, setSearchEstablishment] = useDebounce('');

  const { data: establishmentCompaniesOptions } = useEstablishmentCompanies({
    filters: {
      search: searchEstablishment,
      caModuleEnabled: true,
    },
    mapper: Mapper.mapWithTransformer({
      transformer: EstablishmentCompanyTransformer.optionTransformer()
    })
  });

  const { data: cardDetails, isPending: isLoadingCard, dataUpdatedAt: cardDetailsUpdatedAt } = useCardDetails(debouncedCardIdentifier);

  const { data: companyUsersOptions } = useCompanyUsers({
    filters: {
      search: searchUsername,
      allowedToConsumeOnEquipmentId: cardDetails?.equipment?.id,
    },
    mapper: Mapper.mapToOptions<CompanyUserData>({ labelFieldName: "full_name", valueFieldName: "id" }),
  });

  useEffect(() => {
    if (!cardDetails?.id && !isLoadingCard)
      return clearForm()

    if (cardDetails?.owner) {
      setSearchUsername(cardDetails.owner.full_name);
      setValue('buyer_id', { value: cardDetails.owner.id, label: cardDetails.owner.full_name });
    } else if (isLoadingCard) {
      setValue('buyer_id', null as never);
    }
  }, [cardDetails?.id, isLoadingCard])

  useEffect(() => {
    setValue('category_bypass_consumption_usage_validations', !!cardDetails?.equipment?.model?.category?.bypass_consumption_usage_validations);
  }, [cardDetailsUpdatedAt]);

  return (
    <div className="flex flex-col gap-6 h-full pl-1">
      <p className="text-[20px] font-bold">
        Insira ou digite o código do cartão
      </p>

      <TextField
        data-testid="identifier_code_input"
        label="Código do cartão ou Placa do equipamento"
        errorMessage={errors?.card_id?.message}
        defaultValue={cardIdentifierFromForm}
        onChange={(e) => {
          const value = (e.target as HTMLInputElement).value.replace(/\s+/g, '');

          clearForm();

          setValue('card_identifier', value);
          setDebouncedCardData(value);
        }}
        isLoading={isLoadingCard && !!debouncedCardIdentifier}
        markAsRequired
      />

      {!isLoadingCard && !!debouncedCardIdentifier && (
        <>
          <EquipmentInfo
            cardDetails={cardDetails}
            setValue={setValue}
            companyData={cardDetails?.company as unknown as CompanyData}
          />

          {cardDetails?.id && (cardStatusAvailableToConsumption).includes(cardDetails.status) && (
            <>
              <SelectController
                control={control}
                name="establishment_branch_id"
                dataTestid="establishment_branch_id_selector"
                label="Estabelecimento"
                errorMessage={errors?.establishment_branch_id?.message}
                options={establishmentCompaniesOptions}
                onInputChange={setSearchEstablishment}
                markAsRequired
              />

              <SelectController
                control={control}
                name="buyer_id"
                dataTestid="buyer_id_selector"
                label="Usuário"
                errorMessage={errors?.buyer_id?.message}
                onInputChange={(e) => setSearchUsername(e)}
                isDisabled={!!cardDetails?.owner}
                options={companyUsersOptions}
                markAsRequired
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default EssentialInformationForm;
